import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import useResponsive from '@/hooks/useResponsive';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { getProfile } from '@/redux/parcel/asyncRequest';
import { AppDispatch, RootState } from '@/redux/store';

import type { IReqProfile } from '@/types/parcel/userData/profile.interface';

import { IEditProfileProps } from '.';
import { EditProfile } from './interfaces';
import { getSchemaByCountry, SchemaType } from './schema';
import { formatCompanyToInvoicing } from './utils/invoicingTransform';

const useEditProfile = ({ openCompany }: IEditProfileProps) => {
  const { profile } = useSelector((state: RootState) => state.parcel);
  const country = profile.data?.country;
  const isDesktop = useResponsive('up', 'xxl');
  const isMobile = useResponsive('down', 'sm');
  const [value, setValue] = useState(openCompany ? 1 : 0);
  const [selectedCountryPhone, setSelectedCountryPhone] = useState('MX');
  const dispatch = useDispatch<AppDispatch>();

  const schema = useMemo<import('yup').ObjectSchema<SchemaType>>(
    () => getSchemaByCountry(country ?? '') as import('yup').ObjectSchema<SchemaType>,
    [country],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    watch,
    setValue: setValueForm,
  } = useForm<SchemaType>({
    resolver: yupResolver(schema),
    defaultValues: {
      profile: profile.data?.profile,
      company: profile.data?.company || {},
    },
    context: {
      country: selectedCountryPhone,
    },
    mode: 'onBlur',
  });

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const onSubmit = async (data: EditProfile, section: 'profile' | 'company') => {
    const http = initializeHttpProvider();
    try {
      let payload: IReqProfile;

      if (section === 'profile') {
        // Actualización de perfil personal - solo firstName, lastName y phone
        payload = {
          profile: {
            firstName: data.profile.firstName,
            lastName: data.profile.lastName,
            phone: data.profile.phone,
          },
        };
      } else {
        // Transformar company a invoicing para actualización
        const invoicing = formatCompanyToInvoicing(data.company, country ?? 'MEX');
        payload = { invoicing };
      }

      await http.userData.updateProfile(payload);

      // Forzar la recarga del perfil después de actualizar
      dispatch(getProfile({ forceRefresh: true }));

      toast.success('Perfil actualizado correctamente');
    } catch {
      toast.error('Error al actualizar el perfil');
    }
  };

  const handleButtonClick = async (section: 'profile' | 'company') => {
    handleSubmit(
      (data) => onSubmit(data as EditProfile, section),
      () => onSubmit(getValues() as EditProfile, section),
    )();
  };

  const handleChangeSelectedCountryPhone = (country: string) => {
    setSelectedCountryPhone(country);
  };

  // Watch for companyType changes to reset dependent fields
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      // Only reset if companyType changed and it's for Mexico
      if (name === 'company.companyType' && country === 'MEX') {
        setValueForm('company.taxRegime', '', { shouldValidate: false });
        setValueForm('company.cfdiUse', '', { shouldValidate: false });
        setValueForm('company.rfc', '', { shouldValidate: false });
        setValueForm('company.taxRegimeDescription', '', { shouldValidate: false });
        setValueForm('company.cfdiDescription', '', { shouldValidate: false });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValueForm, country]);

  return {
    register,
    handleSubmit,
    errors,
    value,
    handleChange,
    isDesktop,
    isMobile,
    control,
    getValues,
    watch,
    submitForm: handleButtonClick,
    handleChangeSelectedCountryPhone,
    setValue: setValueForm,
  };
};

export default useEditProfile;
